import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { useDeclineQuoteMutation } from "../Quotes.generated.js";

interface FormValues {
  reason: string;
}

interface Props {
  projectId: string;
  quoteId: string;
  title?: string;
  handleClose: () => void;
  handleComplete?: () => Promise<unknown> | unknown;
}

export const DeclineQuoteModal: React.FC<Props> = ({
  projectId,
  quoteId,
  title,
  handleClose,
  handleComplete,
}) => {
  const { t } = useTranslate(["QuoteDecline", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [declineQuote] = useDeclineQuoteMutation({ client });

  const onSubmit = async (values: FormValues) => {
    await declineQuote({
      variables: {
        input: {
          projectId,
          docId: quoteId,
          reason: values.reason,
        },
      },
    });

    enqueueSnackbar(
      t("Quote declined.", {
        ns: "QuoteDecline",
      })
    );
    handleComplete?.();
  };

  const formId = React.useMemo(() => uniqueId(), []);

  const validationSchema = Yup.object().shape({
    reason: Yup.string().label(
      t("Reason", {
        ns: "QuoteDecline",
      })
    ),
  });

  return (
    <Modal
      title={
        title ??
        t("Decline Quote", {
          ns: "QuoteDecline",
        })
      }
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", {
            ns: "Global",
          }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Decline Quote", {
            ns: "QuoteDecline",
          }),
          buttonProps: {
            type: "submit",
            form: formId,
          },
        },
      ]}
    >
      <Formik<FormValues>
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={{
          reason: "",
        }}
      >
        <Form id={formId}>
          <Stack spacing={1} direction="column">
            <TextField
              autoFocus
              label={t("Reason", {
                ns: "QuoteDecline",
              })}
              name="reason"
              multiline
              rows={3}
            />
          </Stack>
        </Form>
      </Formik>
    </Modal>
  );
};
